import http from '@utils/http';
import { useAuthStore } from '@stores/auth.store';
import { useMessage } from '@context/MessageProvider';
import { useNotification } from '@context/NotificationProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getPublicPlans,
    onboardingClient,
    onboardingCompany,
    acceptInvitation,
    acceptClientInvitation,
} from '@services/auth.service';
import { switchPlan } from '@services/plans.service';
import { PROJECTS_QUERY_KEY } from '@hooks/projects/useProjects';

// Signup (api/v1/auth/signup) – does not log in; redirect to verify-email is handled in useSignup
export const useSignupMutation = () => {
    const { showMessage } = useMessage();
    return useMutation({
        mutationFn: async (payload) => {
            const response = await http.post('/auth/signup', payload);
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'Check your email for the verification code');
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Signup failed', 6);
        },
    });
};

// Verify email (api/v1/auth/verify-email) – on success go to onboarding
export const useVerifyEmailMutation = () => {
    const { showMessage } = useMessage();
    return useMutation({
        mutationFn: async ({ email, otp }) => {
            const response = await http.post('/auth/verify-email', { email, otp });
            const body = response.data;
            const success = body?.data?.success ?? body?.success;
            if (success === false) {
                const err = new Error('OTP incorrect');
                err.response = response;
                throw err;
            }
            return body;
        },
        onSuccess: () => {
            showMessage('success', 'Email verified successfully');
        },
        onError: (error) => {
            const message =
                error?.message === 'OTP incorrect'
                    ? 'OTP incorrect'
                    : error?.response?.data?.message || error?.message || 'Verification failed';
            showMessage('error', message, 6);
        },
    });
};

// Resend verification (api/v1/auth/resend-verification)
export const useResendVerificationMutation = () => {
    const { showMessage } = useMessage();
    return useMutation({
        mutationFn: async (payload) => {
            const response = await http.post('/auth/resend-verification', payload);
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'Verification code sent');
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to resend', 6);
        },
    });
};

export const useLoginMutation = () => {
    const queryClient = useQueryClient();
    const login = useAuthStore((state) => state.login);
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ email, password }) => {
            return await login(email, password);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            showMessage('success', 'Login successful');
        },
        onError: (error) => {
            const msg = error?.message ?? error?.response?.data?.message;
            if (msg === 'EMAIL_NOT_VERIFIED') return;
            showMessage('error', msg || 'Login failed', 6);
        },
    });
};

export const useRegisterMutation = () => {
    const queryClient = useQueryClient();
    const register = useAuthStore((state) => state.register);
    const { showMessage } = useMessage();
    const { showNotification } = useNotification();

    return useMutation({
        mutationFn: async (formData) => {
            return await register(formData);
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            if (response?.routeToLogin) {
                showNotification('success', response.message || 'Registration successful', {
                    duration: 12000,
                    placement: 'bottomRight',
                });
            } else {
                showMessage('success', 'Registration successful', 6);
            }
        },
        onError: (error) => {
            showMessage('error', error.message || 'Registration failed', 6);
        },
    });
};

export const useSendOtpMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async (email) => {
            const response = await http.post('/auth/send-otp', { email });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'OTP sent successfully', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to send OTP', 6);
        },
    });
};

export const useVerifyOtpMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ email, otp }) => {
            const response = await http.post('/auth/password/verify-otp', { email, otp });
            const body = response.data;
            const valid = body?.data?.valid ?? body?.valid;
            if (valid === false) {
                const err = new Error('OTP incorrect');
                err.response = response;
                throw err;
            }
            return body;
        },
        onSuccess: () => {
            showMessage('success', 'OTP verified successfully', 6);
        },
        onError: (error) => {
            const message =
                error?.message === 'OTP incorrect'
                    ? 'OTP incorrect'
                    : error.response?.data?.message || error.message || 'OTP verification failed';
            showMessage('error', message, 6);
        },
    });
};

export const useRequestPasswordResetMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async (email) => {
            const response = await http.post('/auth/password/request-reset', { email });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'If the email exists, an OTP will be sent', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to request reset OTP', 6);
        },
    });
};

export const useResetPasswordMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ email, otp, newPassword }) => {
            const response = await http.post('/auth/password/reset', {
                email,
                otp,
                newPassword,
            });
            return response.data;
        },
        onSuccess: () => {
            showMessage('success', 'Password reset successfully', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Password reset failed', 6);
        },
    });
};

/** POST /api/v1/auth/accept-invitation – accept invite with token, company_id, name, newPassword; returns session like login */
export const useAcceptInvitationMutation = () => {
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: acceptInvitation,
        onSuccess: (data) => {
            const session = data?.data ?? data;
            if (session?.accessToken && session?.user) {
                useAuthStore.getState().setSessionFromVerifyEmail(data);
            }
            showMessage('success', 'Welcome! Redirecting to dashboard.', 6);
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Accept invitation failed', 6);
        },
    });
};

/** POST /api/v1/auth/accept-client-invitation – accept client invite with token, name, password; returns session */
export const useAcceptClientInvitationMutation = () => {
    const { showMessage } = useMessage();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: acceptClientInvitation,
        onSuccess: async (data) => {
            const session = data?.data ?? data;
            if (session?.accessToken && session?.user) {
                useAuthStore.getState().setSessionFromVerifyEmail(data);
                await queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
                await queryClient.refetchQueries({ queryKey: PROJECTS_QUERY_KEY });
            }
            showMessage('success', 'Welcome! Redirecting to dashboard.', 6);
        },
        onError: (error) => {
            showMessage(
                'error',
                error.response?.data?.message || error.message || 'Accept client invitation failed',
                6,
            );
        },
    });
};

export const usePublicPlans = () => {
    return useQuery({
        queryKey: ['plans', 'public'],
        queryFn: getPublicPlans,
    });
};

export const useSwitchPlanMutation = () => {
    const queryClient = useQueryClient();
    const setPlanKey = useAuthStore((state) => state.setPlanKey);
    const { showMessage } = useMessage();

    return useMutation({
        mutationFn: async ({ planKey }) => {
            const data = await switchPlan({ planKey });
            return { data, requestedPlanKey: planKey };
        },
        onSuccess: ({ data, requestedPlanKey }) => {
            const newPlanKey =
                data?.planKey ?? data?.user?.planKey ?? data?.user?.plan ?? data?.plan?.key ?? requestedPlanKey;
            if (newPlanKey) setPlanKey(newPlanKey);
            queryClient.invalidateQueries({ queryKey: ['open-market'] });
            queryClient.invalidateQueries({ queryKey: ['plans', 'public'] });
            showMessage('success', 'Plan updated successfully');
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Failed to switch plan', 6);
        },
    });
};

export const useOnboardingClientMutation = () => {
    const { showMessage } = useMessage();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: onboardingClient,
        onSuccess: async (data) => {
            useAuthStore.getState().setSessionFromVerifyEmail(data);
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            await queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            await queryClient.refetchQueries({ queryKey: PROJECTS_QUERY_KEY });
            showMessage('success', 'Onboarding completed');
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Onboarding failed', 6);
        },
    });
};

export const useOnboardingCompanyMutation = () => {
    const { showMessage } = useMessage();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: onboardingCompany,
        onSuccess: (data) => {
            useAuthStore.getState().setSessionFromVerifyEmail(data);
            queryClient.invalidateQueries({ queryKey: ['auth'] });
            showMessage('success', 'Onboarding completed');
        },
        onError: (error) => {
            showMessage('error', error.response?.data?.message || error.message || 'Onboarding failed', 6);
        },
    });
};
